import { Box, Divider, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';

import styles from '../../../assets/styles/componentStyles/billingStyles/billingCurrentPlanCard.module.scss';

import GlobalButton from '../../utility/globalButton/button';
import { useBillingStore } from '../../../store/billingStore';
import { primaryColor } from '../../../utils/colors';
import { thousandSeparator } from '../../../utils/constants';
import { getAddOnDescription, getAddOnAmount, getAddOnTitle, getBillingCycleName, getSubscriptionPlanAddOns, isAiCreditsAddOn, isSubscriptionOneTimeAddOn, shouldShowAddOns } from '../utils.billing';
import { CYCLE_TYPES } from '../constant.billing';

const CurrentPlanCard = () => {
    
    const navigate = useNavigate();

    const { billingSubscriptionDetails } = useBillingStore(useShallow((state) => ({
        billingSubscriptionDetails: state.billingSubscriptionDetails,
    })));

    const plan = billingSubscriptionDetails?.current_plan;
    const planName = plan?.billing_plan_name || 'No Active Plan';
    const planPrice = Number(plan?.razorpay_plan_amount) || 0;
    const planCurrencySymbol = plan?.billing_plan_currency_symbol || '₹';
    const planCycle = plan?.billing_plan_cycle_period_name || CYCLE_TYPES.MONTHLY;
    const planDescription = plan?.billing_plan_description || '';
    const addOns = getSubscriptionPlanAddOns(plan);

    return (
        <Box className={styles.card}>
            {/* Header Section */}
            <Box className={styles.header}>
                <Box className={styles.headerLeft}>
                    <Typography className={styles.sectionLabel}>
                        Current Plan
                    </Typography>

                    <Typography className={styles.planName}>
                        {planName}
                    </Typography>

                    {planDescription && (
                        <Typography className={styles.planDescription}>
                            {planDescription}
                        </Typography>
                    )}
                </Box>

                <Box className={styles.headerRight}>
                    <Box className={styles.priceRow}>
                        <Typography className={styles.planPrice}>
                            {planCurrencySymbol}{thousandSeparator(planPrice)}
                        </Typography>
                    </Box>

                    <Typography className={styles.billingCycle}>
                        Billed {getBillingCycleName(planCycle)}
                    </Typography>
                </Box>
            </Box>

            {/* Add-ons Section */}
            {plan && shouldShowAddOns(plan) && addOns.length > 0 && (
                <>
                    <Divider className={styles.divider} /> 

                    <Box className={styles.addOnsSection}>
                        <Typography className={styles.addOnsLabel}>
                            Active Add-ons
                        </Typography>

                        <Box className={styles.addOnList}>
                            {addOns.map((addOn, index) => (
                                <Box className={styles.addOnItem} key={`${addOn.billing_add_on_id}-${index}`}>
                                    <Box className={styles.addOnIconWrap}>
                                        {isAiCreditsAddOn(addOn)
                                            ? <BoltOutlinedIcon sx={{ color: primaryColor, fontSize: 20 }} />
                                            : <AutoAwesomeOutlinedIcon sx={{ color: primaryColor, fontSize: 18 }} />
                                        }
                                    </Box>

                                    <Box className={styles.addOnContent}>
                                        <Typography className={styles.addOnTitle}>
                                            {getAddOnTitle(addOn)}
                                        </Typography>

                                        <Typography className={styles.addOnMeta}>
                                            {getAddOnDescription(addOn)}
                                        </Typography>
                                    </Box>

                                    <Typography className={styles.addOnPrice}>
                                        {planCurrencySymbol}{thousandSeparator(getAddOnAmount(addOn))}

                                        {isSubscriptionOneTimeAddOn(plan, addOn) && (
                                            <span className={styles.addOnPriceSuffix}>
                                                {" "}one-time
                                            </span>
                                        )}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </>
            )}

            {/* Action Buttons */}
            <Box className={styles.actions}>
                <GlobalButton
                    buttonText="Change Plan"
                    className="primaryButtonStyle"
                    onClick={() => navigate('/plans')}
                />
            </Box>
        </Box>
    );
};

export default CurrentPlanCard;
